import { useState } from 'react';
import { GlobalToken, message } from 'antd';
import momentTimeZone from 'moment-timezone';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import { EPButton, EPSelect, EPSelectOption } from '@/components';

import PeriodText from './PeriodText';
import timeZonesSelect from './timeZonesSelect';

const DataExportModalContent = observer(({ handleCloseModal }) => {
  const { measurementSystem } = useMeasurement();
  const { t } = useTranslation();
  const { investigationStore } = useStores();

  const timePeriodIntervals = ['0.1', '1', '5', '10', '30', '60'];
  const localTimeZoneOffset = momentTimeZone.tz(momentTimeZone.tz.guess()).utcOffset();
  const [selectedTimeInterval, setSelectedTimeInterval] = useState(timePeriodIntervals[1]);
  const [selectedTimeZone, setSelectedTimeZone] = useState(localTimeZoneOffset);
  const [selectedUnitSystem, setSelectedUnitSystem] = useState(measurementSystem);

  const onChangeTimeInterval = value => {
    setSelectedTimeInterval(value);
  };

  const onChangeUnitSystem = value => {
    setSelectedUnitSystem(value);
  };

  const onChangeTimeZone = value => {
    setSelectedTimeZone(value);
  };

  const onClickExport = async () => {
    const result = await investigationStore.requestDataExport(
      selectedTimeInterval,
      selectedTimeZone,
      selectedUnitSystem,
      t,
    );

    switch (result) {
      case false:
        return;
      case 100:
        message.error(t('Report contains to many records'));
        break;
      case 101:
        message.error(t('Another export is already in progress'));
        break;
      case 999:
        message.error(t('Something went wrong. Make sure you generated a valid report in the investigation tab'));
        break;
      default:
        message.info(t('While your file is being prepared do not refresh or close this tab'));
        break;
    }
  };

  return (
    <StyledModalContent>
      <div className="top">
        <header>
          <div className="main-title">
            {t('Investigation')}
            <span className="name"> | {t('Report export')}</span>
          </div>
        </header>
      </div>

      <div className="dates">
        <PeriodText />
      </div>

      <div className="main">
        <div className="report-export-title">{t('Report export preferences')}</div>

        <div className="report-preferences">
          <div className="time-interval">
            <span className="preference-title">{t('Time presentation interval')}</span>
            <EPSelect
              value={selectedTimeInterval}
              suffixIcon={<IconChevronDown />}
              onChange={onChangeTimeInterval}
              popupClassName="time-presentation-interval"
            >
              {timePeriodIntervals.map(timePeriod => {
                return (
                  <EPSelectOption key={timePeriod}>
                    {timePeriod === '0.1' ? 10 : timePeriod}{' '}
                    {timePeriod === '1' ? t('minute') : timePeriod === '0.1' ? t('seconds') : t('minutes')}
                  </EPSelectOption>
                );
              })}
            </EPSelect>
          </div>

          <div className="time-zone">
            <span className="preference-title">{t('Time zone')}</span>
            <EPSelect
              value={selectedTimeZone}
              suffixIcon={<IconChevronDown />}
              onChange={onChangeTimeZone}
              popupClassName="time-zone"
            >
              {timeZonesSelect().map(timezone => {
                return (
                  <EPSelectOption key={timezone.key} value={timezone.key}>
                    {timezone.text}
                  </EPSelectOption>
                );
              })}
            </EPSelect>
          </div>

          <div className="unit-system">
            <span className="preference-title">{t('Unit system')}</span>
            <EPSelect
              value={selectedUnitSystem}
              suffixIcon={<IconChevronDown />}
              onChange={onChangeUnitSystem}
              popupClassName="unit-system"
            >
              <EPSelectOption key={'METRIC'}>{t('Metric')}</EPSelectOption>
              <EPSelectOption key={'IMPERIAL'}>{t('Imperial')}</EPSelectOption>
            </EPSelect>
          </div>
        </div>

        <div className="file-type-info">{t('This file will be downloaded as a .csv')}</div>
      </div>

      <div className="bottom">
        <EPButton className="btn-close-export" onClick={handleCloseModal}>
          {t('Cancel')}
        </EPButton>

        <EPButton
          className="btn-export"
          onClick={onClickExport}
          type="primary"
          loading={investigationStore.isExportingData}
          disabled={investigationStore.isExportingData}
        >
          {t('Export')}
        </EPButton>
      </div>
    </StyledModalContent>
  );
});

const StyledModalContent = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  .main-title {
    color: ${({ theme: { token } }) => token.colorText};
    font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;

    .name {
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
    }
  }

  .dates {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    color: ${({ theme: { token } }) => token.colorText};

    .selectedPeriods {
      display: flex;
      flex-direction: column;
      margin-top: ${({ theme: { token } }) => token.marginXXS}px;

      .vs {
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
        margin: ${({ theme: { token } }) => `${token.marginXXS}px 0`};
      }
    }
  }

  .main {
    flex-grow: 1;

    .report-export-title {
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      margin-top: ${({ theme: { token } }) => token.marginXXL}px;
      font-weight: normal;
    }

    .report-preferences {
      display: flex;
      margin-top: ${({ theme: { token } }) => token.marginMD}px;

      .ant-select {
        min-width: 130px;
        max-width: 160px;
      }

      .unit-system,
      .time-zone,
      .time-interval {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        color: ${({ theme: { token } }) => token.colorBorder};
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        margin-right: ${({ theme: { token } }) => token.marginMD}px;
        width: 200px;

        .preference-title {
          margin-bottom: ${({ theme: { token } }) => token.marginXS}px;
        }
      }
    }

    .file-type-info {
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
      margin-top: ${({ theme: { token } }) => token.marginXL}px;
    }
  }

  .bottom {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: ${({ theme: { token } }) => token.marginXXL}px;

    .btn-close-export {
      margin-right: ${({ theme: { token } }) => token.marginXL}px;
    }

    .btn-export {
      margin-right: ${({ theme: { token } }) => token.marginXL}px;
    }
  }
`;

export default DataExportModalContent;
